/* eslint-disable indent */
import { useNavigation } from '@react-navigation/native';
import { AppHeaderAction } from '@/components/AppHeader/AppHeaderAction';
import { useEffect, useState } from 'react';
import { ScrollView, View } from 'react-native';
import { Text } from '@/components/Text';
import { Icon } from '@/components/Icon';
import colors from '@/config/colors';
import { logError } from '@/utils/logHandling';
import { fetchPatientsList } from '@/api/services/user';
import { hasInternetAsync } from '@/screens/HomeTherapist/helpers';
import { StackNavigationProp, StackScreenProps } from '@react-navigation/stack';
import { updatePatientSettings } from '@/api/services/patient';
import { TherapistStackParamList } from '@/navigations/TherapistStack';
import store, { useStoreActions, useStoreState } from '@/stores/store';
import { ActiveSets } from './ActiveSets';
import AuditoryLanguageComprehension from './AuditoryLanguageComprehension';
import { DailyGoal } from './DailyGoal';
import DesignateInWriting from './DesignateInWriting';
import OralNaming from './OralNaming';
import ReadingComprehension from './ReadingComprehension';
import { useNotification } from '@/components/ui/therapist/Notification/NotificationProvider';
import { Modal } from '@/components/ui/therapist/Modal';
import { PrimaryButton } from '@/components/Button';
import { Card } from '@/components/ui/therapist/Card';
import { replacedPatientProfileHeader } from '@/utils/stringHelpers';
import { styles } from './styles';

function SelfTrainingSettings(
  props: StackScreenProps<TherapistStackParamList, 'SelfTrainingSettings'>,
) {
  const [active, setAcitve] = useState('');
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);

  const saveSettings = useStoreActions((actions) => actions.saveSettings);
  const { showNotification } = useNotification();

  const patient = useStoreState((state) => state.patient);
  if (!patient) {
    throw new Error('Could find patient');
  }

  const navigation =
    useNavigation<StackNavigationProp<TherapistStackParamList>>();

  const handlePressSave = async () => {
    try {
      activewords();
      saveSettings();
      const patient = store.getState().patient;
      if (!patient) {
        throw new Error();
      }
      const { exercises, therapistTags, ...payload } = patient;

      await updatePatientSettings(payload, props.route.params.id);
      setShowSaveConfirm(true);
    } catch (error) {
      showNotification({
        type: 'error',
        title: 'Einstellungen konnten nicht gespeichert werden.',
      });
      logError('Could not save self training settings', error);
    }
  };

  useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <View style={{ gap: 30, flexDirection: 'row' }}>
          <AppHeaderAction
            primary
            onPress={navigation.goBack}
            icon={<Icon name="close" scale={1.2} />}
            text="Abbrechen"
          />
          <AppHeaderAction
            primary
            testID="self-training-settings-save-button"
            onPress={handlePressSave}
            icon={<Icon name="save" />}
            text="Speichern"
          />
        </View>
      ),
    });
  }, []);

  const onPressSave = async () => {
    const hasInternet = await hasInternetAsync();
    if (!hasInternet) {
      props.navigation.goBack();
      return;
    }
    try {
      const patients = await fetchPatientsList();
      setShowSaveConfirm(false);
      props.navigation.navigate('PatientProfile', {
        id: patient._id,
        patients,
        replacedTitle: replacedPatientProfileHeader({
          patients: undefined,
          patientId: patient._id,
          patient,
        }),
      });
    } catch (error) {
      logError('Could not fetch patients after saving settings', error);
    }
  };

  const activewords = () => {
    let word = '';
    let sentence = '';
    let text = '';

    patient.wordsets.forEach((item) => {
      const removed = patient.activeWordsets.filter((data) => data == item._id);
      if (removed == item._id && patient.activeWordsets.length > 1) {
        word = word + item.name + ', ';
      } else if (removed == item._id) {
        word = word + item.name;
      }
    });

    patient.textsets.forEach((item) => {
      const removed = patient.activeTextsets.filter((data) => data == item._id);
      if (removed == item._id && patient.activeTextsets.length > 1) {
        text = text + item.name + ', ';
      } else if (removed == item._id) {
        text = text + item.name;
      }
    });

    patient.sentencesets.forEach((item) => {
      const removed = patient.activeSentencesets.filter(
        (data) => data == item._id,
      );
      if (removed == item._id && patient.activeSentencesets.length > 1) {
        sentence = sentence + item.name + ', ';
      } else if (removed == item._id) {
        sentence = sentence + item.name;
      }
    });

    word =
      word +
      (sentence.length > 0
        ? (word.length > 0 ? ', ' : '') + sentence
        : sentence) +
      (text.length > 0
        ? (sentence.length > 0 ? ', ' : word.length > 0 ? ', ' : '') + text
        : text);
    setAcitve(word.replace(' ,', ''));
  };
  const ActiveSetsSummary = () => {
    const setsSelected =
      patient.activeTextsets.length > 0 ||
      patient.activeSentencesets.length > 0 ||
      patient.activeWordsets.length > 0;
    return (
      <View style={styles.container}>
        <Icon
          name={setsSelected ? 'checkCircle' : 'alertCircle'}
          size={24}
          color={setsSelected ? colors.primary : colors.red}
        />

        {setsSelected ? (
          <Text style={{ fontSize: 20 }}>
            Die aktiven Übungssets sind:{' '}
            <Text style={[{ fontSize: 20 }, styles.textStyle]}>{active}</Text>
          </Text>
        ) : (
          <Text style={{ fontSize: 20 }}>
            Keine aktiven Übungssets ausgewählt.
          </Text>
        )}
      </View>
    );
  };

  return (
    <>
      <ScrollView style={{ backgroundColor: colors.newUI.therapist.bgBlue10 }}>
        <View style={{ gap: 42, padding: 32 }}>
          {!patient.code ? (
            <Card title="Aphasie-App zum Eigentraining verbinden">
              <Text style={{ fontSize: 14 }}>
                Für die Zuweisung von Eigentrainings auf das Patienten-Gerät
                wird der Patienten-Code benötigt. Dieser kann im
                Patienten-Profil eingetragen werden.
              </Text>
            </Card>
          ) : null}
          <Card title="Übungssets für das Eigentraining aktivieren">
            <ActiveSets />
          </Card>

          <Card title="Tagesziel Übungsdauer">
            <DailyGoal />
          </Card>

          <Card title="Lesesinnverständnis" styleBody={{ padding: 0 }}>
            <ReadingComprehension />
          </Card>

          <Card title="Auditives Sprachverständnis" styleBody={{ padding: 0 }}>
            <AuditoryLanguageComprehension />
          </Card>

          <Card title="Schriftliches Benennen" styleBody={{ padding: 0 }}>
            <DesignateInWriting />
          </Card>

          <Card title="Mündliches Benennen" styleBody={{ padding: 0 }}>
            <OralNaming />
          </Card>
        </View>
      </ScrollView>
      <Modal
        onClose={() => {
          setShowSaveConfirm(false);
          props.navigation.goBack();
        }}
        isVisible={showSaveConfirm}
        confirmActionButton={
          <PrimaryButton
            testID="self-training-settings-saved-button"
            label="Fertig"
            onPress={onPressSave}
          />
        }
        title="Einstellungen für das Eigentraining gespeichert."
      >
        <View>{ActiveSetsSummary()}</View>
      </Modal>
    </>
  );
}

export default SelfTrainingSettings;
